import { AxiosResponseHeaders } from 'axios'
import { saveAs } from 'file-saver'
import isNil from 'lodash/isNil'

import { getIsMobileDevice, getIsMobileIos } from 'Shared/utils/device'

import { DefaultFile, EmptyDefaultFile } from 'Core/interfaces'

/**
	Bug in saveAs: https://github.com/cogniteev/ember-cli-file-saver/issues/10
	If file name doesn't have extension and it has dots (e.g. 01.a - Invoice - Standard Report (Svc Site NL)),
	then file will be downloaded without extension. Adding extension to file name has to solve the issue.
 */
export const saveFile = ({
	data,
	fileName,
	headers
}: {
	data: Blob | string
	fileName?: string | null
	headers?: AxiosResponseHeaders
}) => {
	if (!isNil(fileName) && !headers) {
		saveAs(data, fileName)
		return
	}

	if (headers && fileName) {
		const filename = getDispositionHeaderFilename(headers, fileName)

		saveAs(data, filename)
		return
	}

	const filename = getDispositionHeaderFilename(headers)

	saveAs(data, filename)
}

export const getDispositionHeaderFilename = (
	headers: AxiosResponseHeaders | undefined,
	customFilename?: string
): string | undefined => {
	const dispositionHeader: string | undefined = headers
		? (headers['content-disposition'] as string)
		: undefined

	const originFileName = dispositionHeader
		?.split(';')
		.find((n) => n.includes('filename='))
		?.replace('filename=', '')
		.replace(/["']/g, '')
		.trim()

	if (originFileName && customFilename) {
		return replaceOriginFilenamePartWithCustom(originFileName, customFilename)
	}

	return originFileName
}

export const replaceOriginFilenamePartWithCustom = (
	originFileName: string,
	customFilename: string
): string | undefined => {
	const fileNameParts = originFileName?.split('.')

	if (!fileNameParts) {
		return
	}

	fileNameParts[0] = customFilename

	return fileNameParts?.join('.')
}

export const transformFile = (file: DefaultFile | null) => {
	return Array.isArray(file?.fileList) && file?.fileList[0]?.originFileObj
		? (file?.fileList[0].originFileObj as File)
		: null
}

export const hasNewFile = (file: DefaultFile | EmptyDefaultFile | null) =>
	Array.isArray(file?.fileList) && file?.fileList[0]?.originFileObj

export const shouldSavePrevFile = (file: DefaultFile | EmptyDefaultFile | null) =>
	Array.isArray(file?.fileList) && file?.fileList.length === 0

// if we have file in ArrayBuffer format, the only way to download it - create HTML link
export const downloadFile = (
	data: ArrayBuffer,
	fileName: string = 'File',
	type: string = 'application/pdf',
	headers?: AxiosResponseHeaders
) => {
	let link = document.createElement('a')
	document.body.appendChild(link)
	const newBlob = new Blob([data], { type })
	const url = window.URL.createObjectURL(newBlob)
	link.href = url
	const correctFileName = !!headers ? getDispositionHeaderFilename(headers, undefined) : fileName
	link.download = correctFileName || fileName
	link.click()
	window.URL.revokeObjectURL(url)
	link.remove()
}

export const openDocumentFromBuffer = (data: ArrayBuffer, type: string = 'application/pdf') => {
	const blob = new Blob([data], { type })

	const isMobileDevice = getIsMobileDevice()

	if (isMobileDevice) {
		saveAs(blob, 'document')
		return
	}

	const url = URL.createObjectURL(blob)
	const openPdfButton = document.createElement('a')
	openPdfButton.href = url
	openPdfButton.target = '_blank'
	openPdfButton.click()
	openPdfButton.remove()
}

export const openPdfDocumentFromBlob = async (fileBlob: Blob, fileName?: BasicString) => {
	const isIos = getIsMobileIos()
	const isPdf = fileBlob.type.includes('application/pdf')
	const newBlob = new Blob([fileBlob], { type: fileBlob.type })

	if (isIos || !isPdf) {
		// download file
		const name = fileName || undefined
		saveAs(newBlob, name)
	} else {
		// open PDF in new tab
		const url = URL.createObjectURL(newBlob)
		const openPdfButton = document.createElement('a')
		openPdfButton.href = url
		openPdfButton.target = '_blank'
		openPdfButton.click()
	}
}

export const getFileNameFromFormatName = (formatFileName: string) => {
	if (!formatFileName) {
		return formatFileName
	}

	const splitFileNameArray = formatFileName.split('.')
	splitFileNameArray.pop()

	return splitFileNameArray.join('.')
}
